import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[99999] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  portalClassName?: string;
  showCloseBtn?: boolean;
  showBorder?: boolean;
  closeBtnClassName?: string;
  onBack?: () => void;
  onBackBtnClassName?: string;
  descriptionText?: string;
  descriptionClassName?: string;
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  (
    {
      className,
      portalClassName,
      children,
      showCloseBtn,
      showBorder = false,
      closeBtnClassName,
      onBack,
      onBackBtnClassName,
      descriptionText = "Description",
      descriptionClassName,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay className={portalClassName} />
      <DialogPrimitive.Content
        onInteractOutside={(e) => e.preventDefault()}
        ref={ref}
        className={cn(
          "scrollbar fixed left-[50%] top-[50%] z-[99999] grid translate-x-[-50%] translate-y-[-50%] gap-4 rounded-3xl bg-card-bg p-8 text-white outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          showBorder && "border border-[#242424]",
          className,
        )}
        {...props}
      >
        <DialogDescription className={cn("sr-only", descriptionClassName)}>{descriptionText}</DialogDescription>
        {children}
        {onBack && (
          <button
            onClick={onBack}
            aria-label="back"
            className={cn(
              "absolute left-6 top-6 flex h-7 w-[29px] items-center justify-center rounded-[10px] bg-[#292a2b] transition-opacity disabled:pointer-events-none",
              onBackBtnClassName,
            )}
          >
            <ArrowLeft size={20} />
            <span className="sr-only">Back</span>
          </button>
        )}
        {showCloseBtn && (
          <DialogPrimitive.Close
            aria-label="close-modal"
            className={`absolute right-6 top-6 flex h-7 w-[29px] items-center justify-center rounded-[10px] bg-[#292a2b] transition-opacity disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground ${closeBtnClassName}`}
          >
            <X size={20} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse items-center", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
